import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {ApiErrorCode} from 'spotcharge-types';
import {ToastProvider} from '@core/providers/toast.provider';
import {getErrorMessage, toApiError} from '@core/utils/api-error-handler';
import {
  CHARGE_CODE_RETURN_INTENT_KEY,
  ChargeCodeReturnIntent,
} from '@core/models/charge-code-return.intent';
import {GuestSessionService} from './guest-session.service';
import {StationChargeCodeHttpService} from './station-charge-code-http.service';
import {StorageService} from './storage.service';

export interface ChargeCodeStationRef {
  id: string;
  name?: string | null;
  chargeCodeEnabled: boolean;
}

export interface ChargeCodeGateOptions {
  /** Where the hardware back button returns (station or charger detail). */
  backTo: string;
  /** Checkout route after unlock (`/charge-now` or `/booking-options`). */
  returnRoute: string;
  chargerId: string;
  clearBookingOnBack?: boolean;
}

const CODE_CHANGED_NOTICE =
  'The charge code for this station has changed. Enter the new code to continue.';

/**
 * Entrance to Charge now and Book for later. Navigates to the enter-charge-code page when
 * this account has not entered the station's current code.
 */
@Injectable({
  providedIn: 'root',
})
export class StationChargeCodeEntranceService {
  private router = inject(Router);
  private chargeCodeHttp = inject(StationChargeCodeHttpService);
  private guestSession = inject(GuestSessionService);
  private toastProvider = inject(ToastProvider);
  private storage = inject(StorageService);

  /**
   * True when checkout may open. Guests at a code station go to login first; `backTo` is the return URL.
   */
  async ensureUnlocked(station: ChargeCodeStationRef, options: ChargeCodeGateOptions): Promise<boolean> {
    if (!station.chargeCodeEnabled) {
      return true;
    }
    if (!(await this.guestSession.requireLogin(options.backTo))) {
      return false;
    }
    try {
      const status = await firstValueFrom(this.chargeCodeHttp.status(station.id));
      if (!status.required) {
        return true;
      }
      await this.openCodePage(station, {
        ...options,
        notice: null,
        lockedUntil: status.lockedUntil,
        resumeCheckoutOnly: false,
      });
      return false;
    } catch (error) {
      await this.toastProvider.presentToast({message: getErrorMessage(error), color: 'danger'});
      return false;
    }
  }

  isCodeRequiredError(error: unknown): boolean {
    return toApiError(error).code === ApiErrorCode.STATION_CHARGE_CODE_REQUIRED;
  }

  /** Booking insert rejected because the code changed — no booking row exists. */
  async reenterAfterCodeChange(
    stationId: string,
    options: {
      returnRoute: string;
      backTo: string;
      stationName?: string | null;
      chargerId?: string;
    },
  ): Promise<void> {
    await this.openCodePage(
      {id: stationId, name: options.stationName ?? null, chargeCodeEnabled: true},
      {
        backTo: options.backTo,
        returnRoute: options.returnRoute,
        chargerId: options.chargerId ?? '',
        notice: CODE_CHANGED_NOTICE,
        lockedUntil: null,
        resumeCheckoutOnly: true,
      },
    );
  }

  private async openCodePage(
    station: ChargeCodeStationRef,
    intent: ChargeCodeReturnIntent & {
      notice: string | null;
      lockedUntil: string | null;
    },
  ): Promise<void> {
    const {notice, lockedUntil, ...returnIntent} = intent;
    await this.storage.set(CHARGE_CODE_RETURN_INTENT_KEY, returnIntent);
    await this.router.navigate(['/enter-charge-code', station.id], {
      queryParams: {
        stationName: station.name ?? undefined,
        backTo: intent.backTo,
        notice: notice ?? undefined,
        lockedUntil: lockedUntil ?? undefined,
      },
    });
  }
}
